import React from 'react';
import styled from 'styled-components/macro';

import {
  useTheme,
  alignCenter,
  trackingId,
  forcedColorsMedia,
  useResponsiveCtx,
} from '@blueprint/ui';

import Box from '../Box';

import Announcer from './Announcer';
import SpinnerLabel from './SpinnerLabel';
import SpinnerWrapper from './SpinnerWrapper';
import CircularProgress from './CircularProgress';

const SpinnerIconbox = styled(Box)`
  ${alignCenter}
  ${forcedColorsMedia()`
    svg{
      fill: CanvasText !important;
    }
  `}
`;

const CircularProgressEnhanced = styled(CircularProgress)`
  ${forcedColorsMedia()`
    color: CanvasText !important;
  `}
`;

/**
 * Spinner
 */
const Spinner = ({
  variant = 'light.medium',
  label,
  hideLabel = false,
  labelPosition = 'bottom',
  color: propsColor,
  spinnerType = 'indeterminate',
  value = 0,
  children,
  ...rest
}) => {
  const { hMedia } = useResponsiveCtx();
  const [labelMode, labelSize] = variant.split('.');
  const thickness = useTheme('spinner.base.thickness');
  const color = useTheme(`spinner.color.${labelMode}`);
  const size = useTheme(
    `spinner.${
      hMedia ? 'hBreakpoints' : 'compact'
    }.variantMapping.size.${labelSize}`,
  );

  // announce the label content
  const announcer = label && <Announcer repeat>{label}</Announcer>;
  // should show label
  const showLabel = label && !hideLabel;

  // get the icon component if passed through
  const iconComponent =
    React.isValidElement(children) &&
    React.cloneElement(children, { size: '100%', fill: color });

  // enable props override
  const colorToUse = propsColor || color;

  return (
    <SpinnerWrapper
      labelPosition={labelPosition}
      display="flex"
      data-testid="Spinner"
      data-trackid={trackingId('Spinner')}
      position="relative"
      alignItems="center"
      flexDirection="row-reverse"
      {...rest}
    >
      {announcer}

      <Box position="relative" size={size}>
        <CircularProgressEnhanced
          thickness={thickness}
          size={size}
          color={colorToUse}
          variant={spinnerType}
          value={value}
        />
        <SpinnerIconbox size="18px">{iconComponent}</SpinnerIconbox>
      </Box>

      {showLabel && (
        <SpinnerLabel
          labelPosition={`${labelSize}.${labelPosition}`}
          noWrap
          mode={labelMode}
          variant={labelSize}
          color={colorToUse}
          hMedia={hMedia}
        >
          {label}
        </SpinnerLabel>
      )}
    </SpinnerWrapper>
  );
};

export default Spinner;
